<template>
  <div class="menu-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>菜单管理</span>
          <el-button type="primary" @click="handleCreate">新增菜单</el-button>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="菜单名称">
          <el-input
            v-model="queryParams.menuName"
            placeholder="请输入菜单名称"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="平台类型">
          <el-select v-model="queryParams.platformType" placeholder="全部" clearable style="width: 150px">
            <el-option label="PC端" value="PC" />
            <el-option label="小程序" value="MINI" />
          </el-select>
        </el-form-item>
        <el-form-item label="菜单类型">
          <el-select v-model="queryParams.menuType" placeholder="全部" clearable style="width: 150px">
            <el-option label="目录" value="DIRECTORY" />
            <el-option label="菜单" value="MENU" />
            <el-option label="按钮" value="BUTTON" />
          </el-select>
        </el-form-item>
        <el-form-item label="角色类型">
          <el-select v-model="queryParams.roleType" placeholder="全部" clearable style="width: 150px">
            <el-option label="全部" value="" />
            <el-option label="业务员" value="SALESMAN" />
            <el-option label="管理员" value="MANAGER" />
            <el-option label="会计" value="ACCOUNTANT" />
            <el-option label="系统管理员" value="SYSTEM_ADMIN" />
            <el-option label="内勤" value="ASSISTANT" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="可见性">
          <el-select v-model="queryParams.visible" placeholder="全部" clearable style="width: 120px">
            <el-option label="显示" :value="1" />
            <el-option label="隐藏" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="所属企业" v-if="isSystemAdmin">
          <el-select v-model="queryParams.companyId" placeholder="全部" clearable style="width: 200px">
            <el-option
              v-for="company in companyList"
              :key="company.id"
              :label="company.companyName"
              :value="company.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 菜单列表 -->
      <el-table
        v-loading="loading"
        :data="menuList"
        border
        style="width: 100%"
        row-key="id"
        :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
        default-expand-all
      >
        <el-table-column prop="menuName" label="菜单名称" width="200" />
        <el-table-column prop="menuCode" label="菜单编码" width="150" />
        <el-table-column prop="menuType" label="菜单类型" width="100">
          <template #default="{ row }">
            {{ getMenuTypeName(row.menuType) }}
          </template>
        </el-table-column>
        <el-table-column prop="platformType" label="平台类型" width="100">
          <template #default="{ row }">
            {{ getPlatformTypeName(row.platformType) }}
          </template>
        </el-table-column>
        <el-table-column prop="roleType" label="角色类型" width="120">
          <template #default="{ row }">
            {{ row.roleType ? getRoleTypeName(row.roleType) : '全部' }}
          </template>
        </el-table-column>
        <el-table-column prop="companyName" label="所属企业" width="150" v-if="isSystemAdmin" />
        <el-table-column prop="path" label="路由路径" width="200" />
        <el-table-column prop="sortOrder" label="排序" width="80" />
        <el-table-column prop="visible" label="可见性" width="100">
          <template #default="{ row }">
            <el-tag :type="row.visible === 1 ? 'success' : 'info'">
              {{ row.visible === 1 ? '显示' : '隐藏' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.pageNum"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑菜单对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="menuFormRef"
        :model="menuForm"
        :rules="menuFormRules"
        label-width="100px"
      >
        <el-form-item label="菜单名称" prop="menuName">
          <el-input v-model="menuForm.menuName" />
        </el-form-item>
        <el-form-item label="菜单编码" prop="menuCode">
          <el-input v-model="menuForm.menuCode" />
        </el-form-item>
        <el-form-item label="菜单类型" prop="menuType">
          <el-select v-model="menuForm.menuType" style="width: 100%">
            <el-option label="目录" value="DIRECTORY" />
            <el-option label="菜单" value="MENU" />
            <el-option label="按钮" value="BUTTON" />
          </el-select>
        </el-form-item>
        <el-form-item label="平台类型" prop="platformType">
          <el-select v-model="menuForm.platformType" style="width: 100%">
            <el-option label="PC端" value="PC" />
            <el-option label="小程序" value="MINI" />
          </el-select>
        </el-form-item>
        <el-form-item label="角色类型" prop="roleType">
          <el-select v-model="menuForm.roleType" clearable placeholder="全部角色" style="width: 100%">
            <el-option label="业务员" value="SALESMAN" />
            <el-option label="管理员" value="MANAGER" />
            <el-option label="会计" value="ACCOUNTANT" />
            <el-option label="系统管理员" value="SYSTEM_ADMIN" />
            <el-option label="内勤" value="ASSISTANT" />
          </el-select>
        </el-form-item>
        <el-form-item label="所属企业" prop="companyId">
          <el-select
            v-model="menuForm.companyId"
            placeholder="请选择企业（可选）"
            clearable
            filterable
            style="width: 100%"
            :disabled="!isSystemAdmin"
          >
            <el-option
              v-for="company in companyList"
              :key="company.id"
              :label="company.companyName"
              :value="company.id"
            />
          </el-select>
          <div v-if="!isSystemAdmin" style="color: #909399; font-size: 12px; margin-top: 4px;">
            非系统管理员创建的菜单将自动关联到当前企业
          </div>
        </el-form-item>
        <el-form-item label="会员专属" prop="isMemberOnly">
          <el-radio-group v-model="menuForm.isMemberOnly">
            <el-radio :label="0">否</el-radio>
            <el-radio :label="1">是</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="父菜单" prop="parentId">
          <el-tree-select
            v-model="menuForm.parentId"
            :data="menuTreeData"
            :props="{ label: 'menuName', value: 'id', children: 'children' }"
            placeholder="请选择父菜单（可选择任意级别的菜单）"
            clearable
            style="width: 100%"
            :render-after-expand="false"
            check-strictly
            :default-expand-all="true"
            @change="handleParentMenuChange"
          />
        </el-form-item>
        <el-form-item label="路由路径" prop="path">
          <el-input v-model="menuForm.path" />
        </el-form-item>
        <el-form-item label="组件路径" prop="component">
          <el-input v-model="menuForm.component" />
        </el-form-item>
        <el-form-item label="图标" prop="icon">
          <el-input v-model="menuForm.icon" />
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="menuForm.sortOrder" :min="0" />
        </el-form-item>
        <el-form-item label="是否可见" prop="visible">
          <el-radio-group v-model="menuForm.visible">
            <el-radio :label="1">显示</el-radio>
            <el-radio :label="0">隐藏</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="menuForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getMenuList,
  getMenuTree,
  getMenuById,
  createMenu,
  updateMenu,
  deleteMenu
} from '../api/sysMenu'
import { getAllCompanies } from '../api/company'

// 数据
const loading = ref(false)
const menuList = ref([])
const total = ref(0)
const menuTreeData = ref([])
const companyList = ref([]) // 企业列表
const isSystemAdmin = ref(false) // 是否为系统管理员
const queryParams = reactive({
  menuName: '',
  menuType: '',
  platformType: '',
  roleType: '',
  status: null,
  visible: null,
  companyId: null,
  pageNum: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const dialogTitle = ref('新增菜单')
const isEdit = ref(false)
const menuFormRef = ref(null)
const menuForm = reactive({
  id: null,
  menuName: '',
  menuCode: '',
  menuType: 'MENU',
  platformType: 'PC',
  roleType: '',
  isMemberOnly: 0,
  companyId: null,
  parentId: null,
  path: '',
  component: '',
  icon: '',
  sortOrder: 0,
  visible: 1,
  status: 1
})

const menuFormRules = {
  menuName: [{ required: true, message: '请输入菜单名称', trigger: 'blur' }],
  menuCode: [{ required: true, message: '请输入菜单编码', trigger: 'blur' }],
  menuType: [{ required: true, message: '请选择菜单类型', trigger: 'change' }],
  platformType: [{ required: true, message: '请选择平台类型', trigger: 'change' }]
}

// 方法
const getMenuTypeName = (type) => {
  const typeMap = {
    'DIRECTORY': '目录',
    'MENU': '菜单',
    'BUTTON': '按钮'
  }
  return typeMap[type] || type
}

const getPlatformTypeName = (type) => {
  const typeMap = {
    'PC': 'PC端',
    'MINI': '小程序'
  }
  return typeMap[type] || type
}

const getRoleTypeName = (type) => {
  const typeMap = {
    'SALESMAN': '业务员',
    'MANAGER': '管理员',
    'ACCOUNTANT': '会计',
    'SYSTEM_ADMIN': '系统管理员',
    'ASSISTANT': '内勤'
  }
  return typeMap[type] || type
}

const loadMenuList = async () => {
  loading.value = true
  try {
    const res = await getMenuList(queryParams)
    if (res.code === '000000') {
      menuList.value = res.data.records || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取菜单列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 过滤菜单树，排除当前编辑的菜单及其子菜单（避免选择自己作为父菜单）
const filterMenuTree = (tree, excludeId) => {
  if (!tree || tree.length === 0) return []
  
  const filtered = []
  for (const menu of tree) {
    if (menu.id === excludeId) {
      // 跳过当前菜单本身
      continue
    }
    
    const filteredMenu = { ...menu }
    if (menu.children && menu.children.length > 0) {
      filteredMenu.children = filterMenuTree(menu.children, excludeId)
    }
    filtered.push(filteredMenu)
  }
  return filtered
}

const loadMenuTree = async (excludeId = null) => {
  try {
    // 添加时间戳参数，确保不缓存
    const res = await getMenuTree(null)
    if (res.code === '000000') {
      // 如果需要排除某个菜单（编辑时避免选择自己作为父菜单）
      if (excludeId) {
        menuTreeData.value = filterMenuTree(res.data || [], excludeId)
      } else {
        menuTreeData.value = res.data || []
      }
      console.log('菜单树加载成功，菜单数量:', menuTreeData.value.length)
    } else {
      console.error('获取菜单树失败:', res.message)
    }
  } catch (error) {
    console.error('获取菜单树失败:', error)
  }
}

const handleSearch = () => {
  queryParams.pageNum = 1
  loadMenuList()
}

const handleReset = () => {
  queryParams.menuName = ''
  queryParams.menuType = ''
  queryParams.platformType = ''
  queryParams.roleType = ''
  queryParams.status = null
  queryParams.visible = null
  queryParams.companyId = null
  queryParams.pageNum = 1
  loadMenuList()
}

// 加载企业列表
const loadCompanyList = async () => {
  try {
    const res = await getAllCompanies()
    if (res.code === '000000') {
      companyList.value = res.data || []
    }
  } catch (error) {
    console.error('获取企业列表失败:', error)
  }
}

const handleCreate = async () => {
  dialogTitle.value = '新增菜单'
  isEdit.value = false
  Object.assign(menuForm, {
    id: null,
    menuName: '',
    menuCode: '',
    menuType: 'MENU',
    platformType: 'PC',
    roleType: '',
    isMemberOnly: 0,
    companyId: null,
    parentId: null,
    path: '',
    component: '',
    icon: '',
    sortOrder: 0,
    visible: 1,
    status: 1
  })
  // 重新加载菜单树，确保数据最新（包括新增的顶级目录）
  await loadMenuTree()
  // 等待菜单树数据更新
  await nextTick()
  dialogVisible.value = true
}

// 处理父菜单选择变化
const handleParentMenuChange = (value) => {
  console.log('父菜单选择变化:', value)
  // 确保 parentId 正确设置
  if (value === null || value === undefined || value === '') {
    menuForm.parentId = null
  } else {
    menuForm.parentId = value
  }
  console.log('当前菜单表单 parentId:', menuForm.parentId)
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑菜单'
  isEdit.value = true
  try {
    const res = await getMenuById(row.id)
    if (res.code === '000000') {
      Object.assign(menuForm, res.data)
      // 确保 parentId 是数字类型
      if (menuForm.parentId === '' || menuForm.parentId === undefined) {
        menuForm.parentId = null
      } else {
        menuForm.parentId = Number(menuForm.parentId)
      }
      console.log('编辑菜单，当前 parentId:', menuForm.parentId)
      // 重新加载菜单树，过滤掉当前菜单（避免选择自己作为父菜单）
      await loadMenuTree(row.id)
    }
    dialogVisible.value = true
  } catch (error) {
    console.error('获取菜单详情失败:', error)
  }
}

const handleSubmit = async () => {
  if (!menuFormRef.value) return
  await menuFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        // 确保 parentId 正确设置：如果没有选择父菜单，设置为 null（顶级目录）
        const submitData = { ...menuForm }
        if (submitData.parentId === '' || submitData.parentId === undefined || submitData.parentId === null) {
          submitData.parentId = null
        } else {
          const parentIdNum = Number(submitData.parentId)
          // 如果转换后的值为 0 或无效值，也设置为 null
          if (isNaN(parentIdNum) || parentIdNum <= 0) {
            submitData.parentId = null
          } else {
            submitData.parentId = parentIdNum
          }
        }
        console.log('提交菜单数据:', submitData)
        
        const res = isEdit.value ? await updateMenu(submitData) : await createMenu(submitData)
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          // 先重新加载菜单树和列表
          await loadMenuList()
          await loadMenuTree()
          // 立即触发菜单更新事件，通知左侧菜单刷新
          console.log('触发菜单更新事件（保存后）')
          // 使用 CustomEvent 确保事件能正确传递
          const event = new CustomEvent('menu-updated', { 
            detail: { action: isEdit.value ? 'update' : 'create', menuId: submitData.id },
            bubbles: true,
            cancelable: true
          })
          window.dispatchEvent(event)
          // 延迟再次触发，确保事件被捕获
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('menu-updated', { 
              detail: { action: isEdit.value ? 'update' : 'create', menuId: submitData.id },
              bubbles: true 
            }))
          }, 100)
        } else {
          ElMessage.error(res.message || '保存失败')
        }
      } catch (error) {
        console.error('保存菜单失败:', error)
        ElMessage.error('保存菜单失败：' + (error.message || '未知错误'))
      }
    }
  })
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除该菜单吗？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await deleteMenu(row.id)
    if (res.code === '000000') {
      ElMessage.success('删除成功')
      console.log('删除菜单成功，菜单ID:', row.id)
      // 先重新加载菜单列表和树
      await loadMenuList()
      await loadMenuTree()
      // 延迟触发菜单更新事件，确保数据已更新，通知左侧菜单刷新
      setTimeout(() => {
        console.log('触发菜单更新事件（删除后），删除的菜单ID:', row.id)
        // 使用 CustomEvent 确保事件能正确传递
        const event = new CustomEvent('menu-updated', { 
          detail: { action: 'delete', deletedMenuId: row.id },
          bubbles: true 
        })
        window.dispatchEvent(event)
        // 同时触发普通事件作为备用
        window.dispatchEvent(new Event('menu-updated'))
        console.log('菜单更新事件已触发')
      }, 500)
    } else {
      ElMessage.error(res.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除菜单失败:', error)
      ElMessage.error('删除菜单失败：' + (error.message || '未知错误'))
    }
  }
}

const handleDialogClose = () => {
  menuFormRef.value?.resetFields()
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadMenuList()
}

const handlePageChange = (page) => {
  queryParams.pageNum = page
  loadMenuList()
}

// 生命周期
onMounted(() => {
  // 检查是否为系统管理员
  const userInfoStr = localStorage.getItem('userInfo')
  if (userInfoStr) {
    const userInfo = JSON.parse(userInfoStr)
    isSystemAdmin.value = userInfo.userType === 'SYSTEM_ADMIN'
  }
  
  // 加载企业列表（所有用户都需要，因为表单中需要显示）
  loadCompanyList()
  
  loadMenuList()
  loadMenuTree()
})
</script>

<style scoped>
.menu-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>

